import React, { useCallback, useEffect, useLayoutEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Typography,
  Card,
  CardContent,
  IconButton,
  Tooltip,
  Grid,
  Avatar,
  Box,
  Divider,
  InputAdornment,
  TextField,
  CircularProgress,
  Paper,
  Stack,
  Slide,
  Grow,
  Popover,
  Dialog,
  DialogContent,
  Skeleton,
  Button,
} from '@mui/material';
import { empty } from '../../Gift/assets/index';
// import Lightbox from "react-awesome-lightbox";
import {
  ArrowBackIosNewRounded,
  ThumbUpSharp,
  VisibilityRounded,
  EmojiEmotions,
  Send,
  Delete,
  StarBorder,
  StarOutlined,
} from '@mui/icons-material';
import {
  useCommentNewsMutation,
  useDeleteGnnCommentMutation,
  useGetAllNewsQuery,
  useGetNewsByIdQuery,
  useLikeNewsMutation,
  useMarkAsTop5NewsMutation,
  useUnlikeNewsMutation,
  useViewNewsMutation,
} from '../gnnApiSlice';
import UserAvatar from '../../../common/UserAvatar';
import { useDispatch, useSelector } from 'react-redux';
import { selectCurrentUser, selectCurrentUserEmpCode, setGnnStatus } from '../../Auth/authSlice';
import { capitalizeFirstLetter } from '../../../utils/capitalizeFirstLetter';
import LikesModal from './LikesModal';
import { variants } from '../../../variants/variants';
import { keyframes, useTheme } from '@emotion/react';
import { styled } from '@mui/system';
import EmojiPicker from 'emoji-picker-react';
import AttachAvatar from '../../../common/AttachAvatar';
import Carousel from 'react-material-ui-carousel';
import useSocket from '../../../hooks/sockets/useSocket';
import { SkelltoneNewsDetails } from './GnnSkeleton';
import { useMediaQuery } from '@mui/material';
import defaultimg from './default.png';
import AttachmentGallery from '../../../common/Utility/AttachmentGallery ';
import { useAbility } from '@casl/react';
import { AbilityContext } from '../../../context/Ability';

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiInputBase-root': {
    borderRadius: 20,
    backgroundColor: theme.palette.background.paper,
    transition: theme.transitions.create(['border-color', 'box-shadow']),
    '&:hover': {
      backgroundColor: theme.palette.background.default,
    },
    '&.Mui-focused': {
      boxShadow: `${theme.palette.primary.main} 0 0 0 2px`,
    },
  },
}));

const NewsDescription = ({ description }) => {
  const [showMore, setShowMore] = useState(false);
  const MAX_LENGTH = 200; // Maximum number of characters to display before "Show More"

  const toggleShowMore = () => {
    setShowMore(!showMore);
  };

  const truncatedDescription =
    description?.length > MAX_LENGTH ? description.substring(0, MAX_LENGTH) + '...' : description;

  return (
    <Typography variant="body2" paragraph>
      <span
        style={{ whiteSpace: 'pre-wrap' }}
        dangerouslySetInnerHTML={{ __html: showMore ? description : truncatedDescription }}
      />
      {description?.length > MAX_LENGTH && (
        <Button
          onClick={toggleShowMore}
          variant="text"
          size="small"
          style={{ display: 'inline', textTransform: 'none', padding: 0, minWidth: 'auto' }}
        >
          {showMore ? 'Collapse' : 'Show more'}
        </Button>
      )}
    </Typography>
  );
};
const NewsDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [comment] = useCommentNewsMutation();
  const [deleteComment] = useDeleteGnnCommentMutation();
  const userEmpCode = useSelector(selectCurrentUserEmpCode);
  const user = useSelector(selectCurrentUser);
  const dispatch = useDispatch();
  const { data: newsData, isLoading, isError } = useGetNewsByIdQuery(id);
  const [likeNews] = useLikeNewsMutation();
  const [commentData, setCommentData] = useState({
    empCode: userEmpCode,
    comment: '',
  });
  const [hover, setHover] = useState(false);
  const [likesModalOpen, setLikesModalOpen] = useState(false);
  const [unlikeNews] = useUnlikeNewsMutation();
  const [viewNews, { isError: viewError }] = useViewNewsMutation();
  // const [liked, setLiked] = useState(false);
  // const [unLiked, setUnLiked] = useState(false);
  // const [commentPosted, setCommentPosted] = useState(false);
  const empCode = user?.empCode;
  const [openPhoto, setOpen] = useState(false);
  const [photoSrc, setPhotoSrc] = useState(null);
  const [currentLikes, setCurrentLikes] = useState([]);
  const [news, setNews] = useState([]);
  const [markAsTop5News] = useMarkAsTop5NewsMutation();
  const ability = useAbility(AbilityContext);
  const shake = keyframes`
  0% { transform: translateX(0); }
  25% { transform: translateX(-10px); }
  50% { transform: translateX(10px); }
  75% { transform: translateX(-10px); }
  100% { transform: translateX(0); }
`;
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down('xs'));
  const isSm = useMediaQuery(theme.breakpoints.down('sm'));
  const isMd = useMediaQuery(theme.breakpoints.down('md'));
  const isLg = useMediaQuery(theme.breakpoints.down('lg'));
  const isXl = useMediaQuery(theme.breakpoints.up('xl'));

  useLayoutEffect(() => {
    if (!isLoading) {
      setNews(newsData);
    }
  }, [newsData, isLoading]);

  const { socket } = useSocket();

  useEffect(() => {
    dispatch(setGnnStatus(false));

    socket.on('new-count', (message) => {
      const updatedNewsData = news.map((news) =>
        news._id === message._id ? { ...news, likes: message.likes, viewCount: message.viewCount } : news
      );
      setNews(updatedNewsData);
    });

    return () => {
      socket.off('new-count');
    };
  }, [dispatch, socket, news]);

  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleEmojiPopover = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleEmojiClick = (emojiObject) => {
    const newText = commentData.comment + emojiObject.emoji;
    setCommentData((prevState) => ({
      ...prevState,
      comment: newText,
      empCode: user?.empCode,
    }));
    setAnchorEl(null);
  };

  const AnimatedGrid = styled(Grid)(({ hover }) => ({
    animation: hover ? `${shake} 1s` : 'none',
    transition: 'transform 0.3s ease-in-out',
    flexDirection: 'column',
    height: '60vh',
    justifyContent: 'center',
    alignItems: 'center',
  }));
  const empCodeData = {
    empCode: empCode,
  };

  const incrementViewCount = async () => {
    try {
      await viewNews({ id, empCodeData });
    } catch (error) {
      console.error('Error incrementing view count:', error);
    }
  };

  useEffect(() => {
    if (!viewError) {
      incrementViewCount();
    } else {
      console.error('Error incrementing view count:', viewError);
    }
  }, [viewError]);

  const handleBackClick = () => {
    navigate(-1);
  };

  const handleLikeClick = async (event, id) => {
    event.stopPropagation();
    try {
      const response = await likeNews({ id, empCodeData });
    } catch (error) {
      console.error('Error liking news:', error);
    }
  };

  const handleUnlikeClick = async (event, id) => {
    event.stopPropagation();
    try {
      const response = await unlikeNews({ id, empCodeData });
    } catch (error) {
      console.error('Error unliking news:', error);
    }
  };

  const isNewsLiked = (news) => news?.likes?.some((like) => like?.empCode === user?.empCode);

  const handleCommentPost = async (id) => {
    try {
      const response = await comment({ id, commentData });
      setCommentData({
        comment: '',
      });
    } catch (error) {
      console.error('Error posting comment:', error);
    }
  };

  const handleCommentChange = (e) => {
    const { name, value } = e.target;
    setCommentData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleKeyPress = useCallback(
    (event) => {
      if (event.key === 'Enter') {
        const trimmedFeedback = commentData.comment.trim();

        if (!event.shiftKey) {
          event.preventDefault();
          if (trimmedFeedback) {
            handleCommentPost(news[0]._id);
            setCommentData({ comment: '' });
          }
        } else {
          setCommentData((prevData) => ({
            ...prevData,
            comment: event.target.value,
          }));
        }
      }
    },
    [commentData, handleCommentPost, news]
  );

  const handleMarkAsTop5Click = async (event, id) => {
    event.stopPropagation();
    try {
      await markAsTop5News({ id });
      navigate(`/home/announcement/gnn/${id}`);
    } catch (error) {
      console.error('Error marking news as top 5:', error);
    }
  };

  if (isLoading) {
    return <SkelltoneNewsDetails />;
  }

  if (isError) {
    return (
      <Typography variant="h5" color="error" align="center">
        Error fetching news[0] data.
      </Typography>
    );
  }

  const handleLikesClick = (event, likes) => {
    event.stopPropagation();
    setCurrentLikes(likes);
    setLikesModalOpen(true);
  };

  const handleDeleteComment = async (newsId, commentId) => {
    try {
      await deleteComment({ newsId, commentId }).unwrap();
    } catch (error) {
      console.error('Error deleting comment:', error);
    }
  };

  return (
    <Grid sx={{ height: `calc(100vh - 200px)` }}>
      <Grid container spacing={1} sx={{ height: '100%', width: '100%' }}>
        <Grid item xl={8} md={8} xs={12} lg={8} height={'100%'}>
          <Card
            sx={{
              // backgroundColor:'grey',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'auto',
              height: '100%',
              width: '100%',
              boxShadow: 'none',
              border: 1,
              borderColor: 'divider',
              borderRadius: '10px',
            }}
          >
            <Grid sx={{ display: 'flex', width: '100%', marginTop: '1rem', paddingRight: '10px' }}>
              <Grid container sx={{ display: 'flex', flexDirection: 'row' }}>
                <Grid item sx={{}}>
                  <Tooltip title={'Back'}>
                    <IconButton size="small" onClick={handleBackClick} sx={{ color: 'grey' }}>
                      <ArrowBackIosNewRounded sx={{}} />
                    </IconButton>
                  </Tooltip>
                </Grid>
                <Grid item sx={{ display: 'flex', alignItems: 'center', paddingLeft: '7px' }}>
                  {news[0]?.categories != 'Internal Posts' ? (
                    <Typography variant="body2" color="text.secondary">
                      {news[0]?.categories.join(', ')}
                    </Typography>
                  ) : (
                    <>
                      {console.log('authoruserphoto', news[0]?.authorUserPhoto)}
                      <Avatar
                        sx={{
                          width: 40,
                          height: 40,
                          mr: 1,
                          border: '1px solid black',
                          img: { objectFit: 'cover', objectPosition: 'top' },
                        }}
                        src={news[0]?.authorUserPhoto}
                        alt="Avatar"
                      />
                      <Typography variant="body2" color="text.secondary">
                        {news[0]?.authorName}
                      </Typography>
                    </>
                  )}
                </Grid>
              </Grid>
              <Grid container justifyContent="flex-end" alignItems={'center'} sx={{ marginTop: '7px' }}>
                <Box sx={{ display: 'flex' }}>
                  {news[0]?.categories != 'Internal Posts' ? (
                    <>
                      {ability.can('update', 'Gnn') || ability.can('manage', 'Gnn') || ability.can('read', 'Admin') ? (
                        <>
                          {news[0]?.top5News ? (
                            <Tooltip title="Remove from Top 5 News">
                              <IconButton
                                size="small"
                                onClick={(event) => handleMarkAsTop5Click(event, news[0]?._id)}
                                sx={{ padding: 0 }}
                              >
                                <StarOutlined sx={{ fontSize: '17px', fontWeight: '500', mr: 1 }} />
                              </IconButton>
                            </Tooltip>
                          ) : (
                            <Tooltip title="Add to Top 5 News">
                              <IconButton
                                size="small"
                                onClick={(event) => handleMarkAsTop5Click(event, news[0]?._id)}
                                sx={{ padding: 0 }}
                              >
                                <StarBorder sx={{ fontSize: '17px', fontWeight: '500', mr: 1 }} />
                              </IconButton>
                            </Tooltip>
                          )}
                        </>
                      ) : null}
                    </>
                  ) : (
                    <></>
                  )}
                </Box>
                <Grid item sx={{ float: 'right' }}>
                  <Typography sx={{ fontSize: '12px', color: 'grey' }}>
                    Posted on {new Date(news[0]?.createdAt).toLocaleDateString()}
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
            <CardContent sx={{}}>
              <Divider />
              <Grid container justifyContent="center">
                <AttachmentGallery
                  attachments={news[0]?.attachments || []}
                  defaultimg={defaultimg}
                  isDetailScreen={true}
                />
              </Grid>
              <Grid
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                  marginBottom: '1rem',
                  marginTop: '1rem',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    width: '5.5rem',
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <IconButton
                    size="small"
                    onClick={(event) =>
                      isNewsLiked(news[0])
                        ? handleUnlikeClick(event, news[0]?._id)
                        : handleLikeClick(event, news[0]?._id)
                    }
                    sx={{
                      transition: 'color 0.3s ease',
                      color: isNewsLiked(news[0]) ? '#0a4fac' : 'grey',
                      '&:hover': {
                        color: '#73c2fb',
                      },
                    }}
                  >
                    <Tooltip title={isNewsLiked(news[0]) ? 'Unlike' : 'Like'}>
                      <ThumbUpSharp sx={{ fontSize: '20px' }} />
                    </Tooltip>
                  </IconButton>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ cursor: 'default' }}
                    onClick={(event) => handleLikesClick(event, news[0].likes)}
                  >
                    {news[0]?.likes?.length || 0}
                  </Typography>
                  <IconButton size="small">
                    <Tooltip title="Views">
                      <VisibilityRounded sx={{ fontSize: '20px', color: 'grey' }} />
                    </Tooltip>
                  </IconButton>
                  <Typography variant="body2" color="text.secondary">
                    {news[0]?.viewCount?.length}
                  </Typography>
                </Box>
              </Grid>

              <Typography variant="body2" paragraph maxHeight={'40vh'} overflow={'auto'}>
                <div dangerouslySetInnerHTML={{ __html: news[0]?.description }} style={{ whiteSpace: 'pre-wrap' }} />
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xl={4} md={4} lg={4} xs={12} height={'100%'}>
          <Card
            sx={{
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              height: '100%',
              boxShadow: 'none',
              borderRadius: '10px',
              border: 1,
              borderColor: 'divider',
            }}
          >
            <Grid sx={{ p: 2, position: 'sticky', borderBottom: 1, borderColor: 'divider' }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                Comments
              </Typography>
            </Grid>
            <CardContent sx={{ overflow: 'auto', height: '76vh' }}>
              {news[0]?.comments?.length > 0 ? (
                news[0]?.comments
                  ?.slice()
                  .reverse()
                  ?.map((comment) => (
                    <Box
                      key={comment._id}
                      sx={{
                        mb: 2,
                        p: 2,
                        backgroundColor: 'background.paper',
                        borderRadius: '8px',
                        border: 1,
                        borderColor: 'divider',
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'row',
                          justifyContent: 'space-between',
                          mb: 1,
                        }}
                      >
                        <Box display="flex" flexDirection="row" alignItems="center">
                          <Avatar
                            sx={{ width: 32, height: 32, mr: 1, img: { objectFit: 'cover', objectPosition: 'top' } }}
                            src={comment.authorUserPhoto}
                          />
                          <Typography variant="body2" color="#0a4fac" sx={{ fontWeight: 'bold' }}>
                            {capitalizeFirstLetter(comment.authorName)}
                          </Typography>
                        </Box>
                        <Typography fontSize="10px" color="grey">
                          {new Date(comment.date).toLocaleDateString()} | {new Date(comment.date).toLocaleTimeString()}
                        </Typography>
                      </Box>
                      {user?.empCode === comment?.empCode ? (
                        <Box sx={{ float: 'right' }}>
                          <IconButton variant="outlined" onClick={() => handleDeleteComment(news[0]._id, comment._id)}>
                            <Delete sx={{ color: 'grey', fontSize: '17px' }} />
                          </IconButton>
                        </Box>
                      ) : (
                        <></>
                      )}
                      <Typography fontSize="15px" sx={{ color: 'text.primary' }}>
                        {comment.comment}
                      </Typography>
                    </Box>
                  ))
              ) : (
                <Grow in={true} timeout={1000}>
                  <Grid
                    container
                    sx={{ display: 'flex', flexDirection: 'column', height: '60vh' }}
                    justifyContent="center"
                    alignItems="center"
                  >
                    <AnimatedGrid
                      container
                      hover={hover ? 1 : 0}
                      onMouseEnter={() => setHover(true)}
                      onMouseLeave={() => setHover(false)}
                    >
                      <img style={{ width: '50%' }} src={empty} alt="Loading..." />
                      <Typography sx={{ fontSize: '12px', p: 2, color: 'rgba(0,0,0,0.7)', float: 'left' }}>
                        No Comments In this Post
                      </Typography>
                    </AnimatedGrid>
                  </Grid>
                </Grow>
              )}
            </CardContent>
            <Grid sx={{ backgroundColor: 'white', p: 2, borderTop: 1, borderColor: 'divider', position: 'sticky' }}>
              <StyledTextField
                multiline
                fullWidth
                id="comment"
                name="comment"
                value={commentData.comment}
                onChange={handleCommentChange}
                placeholder="Type a comment..."
                variant="outlined"
                onKeyPress={(event) => handleKeyPress(event)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <IconButton onClick={handleEmojiPopover} edge="start">
                        <EmojiEmotions />
                      </IconButton>
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => handleCommentPost(news[0]._id)} edge="end">
                        <Send />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                minRows={1}
                sx={{ borderRadius: '8px', boxShadow: 'none' }}
              />
              <Popover
                id="emoji-picker-popover"
                open={open}
                anchorEl={anchorEl}
                onClose={() => setAnchorEl(null)}
                anchorOrigin={{
                  vertical: 'top',
                  horizontal: 'left',
                }}
                transformOrigin={{
                  vertical: 'bottom',
                  horizontal: 'left',
                }}
              >
                <EmojiPicker onEmojiClick={handleEmojiClick} />
              </Popover>
            </Grid>
          </Card>
        </Grid>
      </Grid>
      <LikesModal open={likesModalOpen} onClose={() => setLikesModalOpen(false)} likes={currentLikes} />
    </Grid>
  );
};

export default NewsDetail;
